import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import TopNav from "../components/TopNav";
import { 
    FaLock, 
    FaPlane, 
    FaCheckCircle, 
    FaKey, 
    FaShieldAlt, 
    FaExclamationTriangle,
    FaInfoCircle,
    FaPaperPlane,
    FaSync,
    FaEye,
    FaEyeSlash,
    FaUserLock
} from "react-icons/fa";
import "./ResetPasswordPage.css";

/**
 * ResetPasswordPage Component
 * 
 * A modern, aviation-themed password change interface for FDXVA users.
 * Allows users to securely change their password through a verification process.
 * 
 * Flow:
 * 1. User enters new password twice and verification code
 * 2. System sends verification link to the user's registered email
 * 3. User clicks verification link to complete the password change
 * 
 * Features:
 * - Dark mode support via props.darkmode
 * - Responsive card-based layout
 * - Progress modal for save operations
 * - Password strength indicator
 * - Show/hide password toggle
 * - Input validation with error messaging
 * - Accessible with sequential tab indexing
 * - CAPTCHA-style verification code
 * - Password change history table (filterable, paginated)
 * 
 * CSS classes are prefixed with 'rpp-' (Reset Password Page) to avoid conflicts
 * 
 * @param {Object} props - Component props
 * @param {string} props.page - Current page identifier for navigation
 * @param {boolean} props.darkmode - Whether dark mode is enabled
 */
const ResetPasswordPage = (props) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Form state
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [verificationInput, setVerificationInput] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // User data
    const [userEmail, setUserEmail] = useState("");
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [step, setStep] = useState(1); // 1: Form, 2: Email Sent, 3: Complete
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [progress, setProgress] = useState(0);
    const [modalMessage, setModalMessage] = useState("");
    const [modalIcon, setModalIcon] = useState("loading");
    
    // History table state
    const [passwordHistory, setPasswordHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);

    // Refs for tab focus
    const newPasswordRef = useRef(null);
    const confirmPasswordRef = useRef(null);
    const verificationRef = useRef(null);
    const submitRef = useRef(null);

    /**
     * Generate a random 4-character verification code
     * Uses CRC32-style hash of current timestamp
     */
    const generateVerificationCode = () => {
        const chars = "ABCDEFGHJKMNPQRSTWXYZ23456789";
        let code = "";
        for (let i = 0; i < 4; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    };

    /**
     * Initialize component state on mount
     * Generate verification code and check for verification token in URL
     */
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });

        // Generate initial verification code
        setVerificationCode(generateVerificationCode());
        
        // Get user email from localStorage
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        if (userData.email) {
            setUserEmail(userData.email);
        }
        
        // Check if user arrived via verification link
        const pxverf = searchParams.get("pxverf");
        if (pxverf) {
            handleVerificationLink(pxverf);
        }
        
        // Load password change history
        loadPasswordHistory();
    }, []);

    /**
     * Apply filter to password history when filter text changes
     */
    useEffect(() => {
        if (filterText.trim() === "") {
            setFilteredHistory(passwordHistory);
        } else {
            const filtered = passwordHistory.filter(item => 
                item.date.toLowerCase().includes(filterText.toLowerCase()) ||
                item.status.toLowerCase().includes(filterText.toLowerCase()) ||
                item.method.toLowerCase().includes(filterText.toLowerCase())
            );
            setFilteredHistory(filtered);
        }
        setCurrentPage(1);
    }, [filterText, passwordHistory]);

    /**
     * Load password change history from server
     */
    const loadPasswordHistory = async () => {
        try {
            // Mock data for now - replace with actual API call
            const mockHistory = [
                { id: 1, date: "2025-01-10 14:30", method: "Web Form", status: "Completed" },
                { id: 2, date: "2025-02-01 09:15", method: "Email Verification", status: "Pending" },
            ];
            setPasswordHistory(mockHistory);
            setFilteredHistory(mockHistory);
        } catch (e) {
            console.error("Failed to load password history:", e);
        }
    };

    /**
     * Handle verification link from email
     * @param {string} token - Verification token from URL
     */
    const handleVerificationLink = async (token) => {
        setLoading(true);
        setShowModal(true);
        setProgress(0);
        setModalMessage("Verifying your password change request...");
        setModalIcon("loading");
        
        const interval = setInterval(() => {
            setProgress(prev => Math.min(prev + 15, 90));
        }, 200);

        try {
            const response = await axios.post(
                "/api/user/verify_password_change",
                { token }
            );
            
            clearInterval(interval);
            setProgress(100);
            
            if (response.data?.success) {
                setModalMessage("Password changed successfully!");
                setModalIcon("success");
                setStep(3);
                setSuccess("Your password has been changed successfully. Please use your new password to log in.");
                setTimeout(() => setShowModal(false), 2500);
            } else {
                throw new Error(response.data?.message || "Verification failed or expired");
            }
        } catch (e) {
            clearInterval(interval);
            setProgress(100);
            setModalMessage(e.message || "Verification failed");
            setModalIcon("error");
            setError(e.message || "Verification failed or expired");
            setTimeout(() => setShowModal(false), 2500);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Validate password format (alphanumeric only)
     * @param {string} password - Password to validate
     * @returns {boolean} Whether password is valid
     */
    // const validatePassword = (password) => {
    //     const passwordRegex = /^[a-zA-Z0-9]+$/;
    //     return passwordRegex.test(password);
    // };

    /**
     * Calculate password strength using entropy-based analysis
     * @param {string} password - Password to evaluate
     * @returns {Object} Strength object with score, label, color, and entropy
     */
    const getPasswordStrength = (password) => {
        if (!password) return { score: 0, label: "", color: "", entropy: 0 };

        const length = password.length;

        // Determine character set size based on variety
        let poolSize = 0;
        const hasLower = /[a-z]/.test(password);
        const hasUpper = /[A-Z]/.test(password);
        const hasNumbers = /[0-9]/.test(password);
        const hasSymbols = /[^a-zA-Z0-9]/.test(password);

        if (hasLower) poolSize += 26;
        if (hasUpper) poolSize += 26;
        if (hasNumbers) poolSize += 10;
        if (hasSymbols) poolSize += 32; // Common symbols: !@#$%^&*()_+-=[]{}|;':",./<>?

        // If no variety detected, assume lowercase only
        if (poolSize === 0) poolSize = 26;

        // Calculate entropy: log2(poolSize^length) = length * log2(poolSize)
        const entropy = length * Math.log2(poolSize);

        // Length bonus for longer passwords (encourages longer passwords)
        let lengthBonus = 0;
        if (length >= 8) lengthBonus += 5;
        if (length >= 12) lengthBonus += 5;
        if (length >= 16) lengthBonus += 5;
        if (length >= 20) lengthBonus += 5;

        // Variety bonus for using multiple character types
        let varietyBonus = 0;
        if (hasLower && hasUpper) varietyBonus += 5;
        if (hasNumbers) varietyBonus += 5;
        if (hasSymbols) varietyBonus += 10;

        const totalScore = entropy + lengthBonus + varietyBonus;

        // Determine strength category based on total score
        let score, label, color;

        if (totalScore < 28) {
            score = 1; label = "Very Weak"; color = "#8B0000"; // Dark red
        } else if (totalScore < 36) {
            score = 2; label = "Weak"; color = "#dc3545"; // Red
        } else if (totalScore < 60) {
            score = 3; label = "Fair"; color = "#fd7e14"; // Orange
        } else if (totalScore < 128) {
            score = 4; label = "Pretty Good"; color = "#ffc107"; // Yellow
        } else if (totalScore < 192) {
            score = 5; label = "Good"; color = "#28a745"; // Green
        } else if (totalScore < 256) {
            score = 6; label = "Quite Good"; color = "#20c997"; // Teal
        } else if (totalScore < 320) {
            score = 7; label = "Really Good"; color = "#17a2b8"; // Cyan
        } else if (totalScore < 384) {
            score = 8; label = "Strong"; color = "#6f42c1"; // Purple
        } else if (totalScore < 448) {
            score = 9; label = "Very Strong"; color = "#e83e8c"; // Pink
        } else {
            score = 10; label = "Basically Unbreakable"; color = "#6c757d"; // Gray
        }

        return {
            score,
            label,
            color,
            entropy: Math.round(entropy),
            poolSize,
            hasLower,
            hasUpper,
            hasNumbers,
            hasSymbols
        };
    };

    /**
     * Handle form submission
     * Validates input and initiates password change process
     * @param {Event} e - Form submit event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation
        if (newPassword.length < 6) {
            setError("Password must be at least 6 characters long");
            return;
        }

        // if (!validatePassword(newPassword)) {
        //     setError("Password must contain only letters (a-z, A-Z) and numbers (0-9)");
        //     return;
        // }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (verificationInput.toUpperCase() !== verificationCode) {
            setError("Incorrect verification code");
            setVerificationCode(generateVerificationCode());
            setVerificationInput("");
            return;
        }

        // Show progress modal
        setShowModal(true);
        setProgress(0);
        setModalMessage("Processing your request...");
        setModalIcon("loading");

        const interval = setInterval(() => {
            setProgress(prev => Math.min(prev + 10, 90));
        }, 150);

        try {
            //console.log("Sending: " + newPassword)
            const response = await axios.post(
                "/api/user/change_password",
                { newPassword }
            );

            clearInterval(interval);
            setProgress(100);

            console.log(response.data)

            if (response.data?.success) {
                setModalMessage("Verification email sent!");
                setModalIcon("success");
                setStep(2);
                setSuccess(`A verification email has been sent to ${userEmail}. Please check your inbox to complete the password change.`);
                setTimeout(() => setShowModal(false), 2500);
            } else {
                throw new Error(response.data?.message || "Failed to send verification email");
            }
        } catch (e) {
            clearInterval(interval);
            setProgress(100);
            setModalMessage(e.message || "An error occurred");
            setModalIcon("error");
            setError(e.message || "Failed to process password change request");
            setTimeout(() => setShowModal(false), 2500);
        }
    };

    /**
     * Regenerate verification code
     */
    const handleRegenerateCode = () => {
        setVerificationCode(generateVerificationCode());
        setVerificationInput("");
    };

    /**
     * Reset form to initial state
     */
    const handleStartOver = () => {
        setStep(1);
        setNewPassword("");
        setConfirmPassword("");
        setVerificationInput("");
        setVerificationCode(generateVerificationCode());
        setError(null);
        setSuccess(null);
    };

    /**
     * Navigate to login page after successful password change
     */
    const handleGoToLogin = () => {
        navigate("/login");
    };

    /**
     * Toggle password visibility
     */
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    /**
     * Toggle confirm password visibility
     */
    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // Password strength calculation
    const passwordStrength = getPasswordStrength(newPassword);

    // Pagination calculations
    const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filteredHistory.slice(startIndex, endIndex);

    /**
     * Handle page change for history table
     * @param {number} page - Page number to navigate to
     */
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return(
        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            
            <div className={`rpp-container ${props.darkmode ? "rpp-dark" : ""}`}>
                {/* Header Section */}
                {/* <header className="rpp-header">
                    <div className="rpp-header-content">
                        <FaPlane className="rpp-header-icon" aria-hidden="true" />
                        <h1>Change Password</h1>
                    </div>
                    <p className="rpp-header-subtitle">
                        Securely update your FedEx Virtual Airlines account password
                    </p>
                </header> */}

                {/* Error Message */}
                {error && (
                    <div className="rpp-error-message" role="alert" aria-live="polite">
                        <FaExclamationTriangle aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Success Message */}
                {success && (
                    <div className="rpp-success-message" role="status" aria-live="polite">
                        <FaCheckCircle aria-hidden="true" />
                        <span>{success}</span>
                    </div>
                )}

                {/* Main Content Area */}
                <main className="rpp-main">
                    {/* Step 1: Password Change Form */}
                    {step === 1 && (
                        <div className="rpp-form-section" role="region" aria-label="Password change form">
                            <div className="rpp-card">
                                <div className="rpp-card-header">
                                    <FaLock className="rpp-card-icon" aria-hidden="true" />
                                    <h2>Enter New Password</h2>
                                </div>
                                <div className="rpp-card-body">
                                    <form onSubmit={handleSubmit} className="rpp-form">
                                        {/* Instructions */}
                                        <div className="rpp-instructions">
                                            <FaInfoCircle className="rpp-info-icon" aria-hidden="true" />
                                            <p>
                                                Enter your new password below. We will send a verification 
                                                link to <strong>{userEmail || "your registered email"}</strong>. Your current 
                                                password will not change until you click the verification link.
                                            </p>
                                        </div>

                                        {/* New Password Field */}
                                        <div className="rpp-form-group">
                                            <label htmlFor="rpp-new-password">
                                                <FaLock className="rpp-input-icon" aria-hidden="true" />
                                                New Password
                                            </label>
                                            <div className="rpp-password-wrapper">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    id="rpp-new-password"
                                                    ref={newPasswordRef}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    placeholder="Enter your new password"
                                                    required
                                                    tabIndex={1}
                                                    autoComplete="new-password"
                                                    aria-required="true"
                                                    aria-describedby="rpp-password-help rpp-password-strength"
                                                    // pattern="[a-zA-Z0-9]+"
                                                    minLength={6}
                                                />
                                                <button
                                                    type="button"
                                                    className="rpp-toggle-visibility"
                                                    onClick={togglePasswordVisibility}
                                                    tabIndex={2}
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                >
                                                    {showPassword ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                                                </button>
                                            </div>
                                            
                                            {/* Password Strength Indicator */}
                                            {newPassword && (
                                                <div className="rpp-strength-container" id="rpp-password-strength" aria-live="polite">
                                                    <div className="rpp-strength-bar">
                                                        <div 
                                                            className="rpp-strength-fill"
                                                            style={{
                                                                width: `${(passwordStrength.score / 10) * 100}%`,
                                                                backgroundColor: passwordStrength.color
                                                            }}
                                                        ></div>
                                                    </div>
                                                    <span 
                                                        className="rpp-strength-label"
                                                        style={{ color: passwordStrength.color }}
                                                    >
                                                        {passwordStrength.label}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            <span id="rpp-password-help" className="rpp-form-help">
                                                Use letters, numbers, and symbols for a stronger password. 
                                                Minimum 6 characters.
                                            </span>
                                        </div>

                                        {/* Confirm Password Field */}
                                        <div className="rpp-form-group">
                                            <label htmlFor="rpp-confirm-password">
                                                <FaCheckCircle className="rpp-input-icon" aria-hidden="true" />
                                                Confirm New Password
                                            </label>
                                            <div className="rpp-password-wrapper">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    id="rpp-confirm-password"
                                                    ref={confirmPasswordRef}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    placeholder="Enter your new password again"
                                                    required
                                                    tabIndex={3}
                                                    autoComplete="new-password"
                                                    aria-required="true"
                                                />
                                                <button
                                                    type="button"
                                                    className="rpp-toggle-visibility"
                                                    onClick={toggleConfirmPasswordVisibility}
                                                    tabIndex={4}
                                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                >
                                                    {showConfirmPassword ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}
                                                </button>
                                            </div>
                                            
                                            {/* Password Match Indicator */}
                                            {confirmPassword && (
                                                <div className="rpp-match-indicator" aria-live="polite">
                                                    {newPassword === confirmPassword ? (
                                                        <span className="rpp-match-success">
                                                            <FaCheckCircle aria-hidden="true" /> Passwords match
                                                        </span>
                                                    ) : (
                                                        <span className="rpp-match-error">
                                                            <FaExclamationTriangle aria-hidden="true" /> Passwords do not match
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Verification Code Field */}
                                        <div className="rpp-form-group">
                                            <label htmlFor="rpp-verification">
                                                <FaShieldAlt className="rpp-input-icon" aria-hidden="true" />
                                                Verification Code
                                            </label>
                                            <div className="rpp-verification-container">
                                                <div className="rpp-code-display" aria-label="Verification code to enter">
                                                    <FaKey className="rpp-code-icon" aria-hidden="true" />
                                                    <span className="rpp-code-text">{verificationCode}</span>
                                                    <button 
                                                        type="button" 
                                                        className="rpp-btn-regenerate"
                                                        onClick={handleRegenerateCode}
                                                        tabIndex={5}
                                                        aria-label="Generate new verification code"
                                                    >
                                                        <FaSync aria-hidden="true" />
                                                    </button>
                                                </div>
                                                <input
                                                    type="text"
                                                    id="rpp-verification"
                                                    ref={verificationRef}
                                                    value={verificationInput}
                                                    onChange={(e) => setVerificationInput(e.target.value.toUpperCase())}
                                                    placeholder={`Enter code: ${verificationCode}`}
                                                    required
                                                    maxLength={4}
                                                    tabIndex={6}
                                                    aria-required="true"
                                                    aria-describedby="rpp-verification-help"
                                                />
                                            </div>
                                            <span id="rpp-verification-help" className="rpp-form-help">
                                                Enter the 4-character code shown above to verify you're human
                                            </span>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="rpp-form-actions">
                                            <button
                                                type="submit"
                                                className="rpp-btn rpp-btn-primary"
                                                ref={submitRef}
                                                tabIndex={7}
                                                disabled={loading}
                                                aria-busy={loading}
                                            >
                                                <FaPaperPlane aria-hidden="true" />
                                                Send Verification Email
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* Security Tips Card */}
                            <div className="rpp-card rpp-security-card">
                                <div className="rpp-card-header rpp-header-security">
                                    <FaUserLock className="rpp-card-icon" aria-hidden="true" />
                                    <h2>Password Security Tips</h2>
                                </div>
                                <div className="rpp-card-body">
                                    <ul className="rpp-tips-list">
                                        <li>
                                            <FaCheckCircle className="rpp-tip-icon" aria-hidden="true" />
                                            Use a mix of uppercase and lowercase letters
                                        </li>
                                        <li>
                                            <FaCheckCircle className="rpp-tip-icon" aria-hidden="true" />
                                            Include numbers to increase complexity
                                        </li>
                                        <li>
                                            <FaCheckCircle className="rpp-tip-icon" aria-hidden="true" />
                                            Make your password at least 8 characters long
                                        </li>
                                        <li>
                                            <FaCheckCircle className="rpp-tip-icon" aria-hidden="true" />
                                            Avoid using personal information like names or birthdays
                                        </li>
                                        <li>
                                            <FaCheckCircle className="rpp-tip-icon" aria-hidden="true" />
                                            Don't reuse passwords from other accounts
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Verification Email Sent */}
                    {step === 2 && (
                        <div className="rpp-confirmation-section" role="region" aria-label="Verification email sent">
                            <div className="rpp-card rpp-card-success">
                                <div className="rpp-card-header rpp-header-success">
                                    <FaCheckCircle className="rpp-card-icon" aria-hidden="true" />
                                    <h2>Verification Email Sent</h2>
                                </div>
                                <div className="rpp-card-body">
                                    <div className="rpp-confirmation-content">
                                        <div className="rpp-plane-animation" aria-hidden="true">
                                            <FaPlane className="rpp-flying-plane" />
                                        </div>
                                        <h3>Check Your Inbox</h3>
                                        <p>
                                            We've sent a verification link to <strong>{userEmail}</strong>.
                                        </p>
                                        <ul className="rpp-instructions-list">
                                            <li>Check your email inbox for a message from FDXVA</li>
                                            <li>Click the verification link in the email</li>
                                            <li>Your password will be updated after verification</li>
                                            <li>If you don't see it, check your spam folder</li>
                                        </ul>
                                        <div className="rpp-warning-box">
                                            <FaExclamationTriangle aria-hidden="true" />
                                            <span>
                                                Be sure to whitelist emails from @fdxva.org to receive future communications.
                                            </span>
                                        </div>
                                        <button
                                            onClick={handleStartOver}
                                            className="rpp-btn rpp-btn-secondary"
                                            tabIndex={1}
                                        >
                                            <FaSync aria-hidden="true" />
                                            Start Over
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Password Change Complete */}
                    {step === 3 && (
                        <div className="rpp-complete-section" role="region" aria-label="Password change complete">
                            <div className="rpp-card rpp-card-complete">
                                <div className="rpp-card-header rpp-header-complete">
                                    <FaCheckCircle className="rpp-card-icon" aria-hidden="true" />
                                    <h2>Password Changed Successfully</h2>
                                </div>
                                <div className="rpp-card-body">
                                    <div className="rpp-complete-content">
                                        <div className="rpp-success-animation" aria-hidden="true">
                                            <div className="rpp-checkmark-circle">
                                                <FaCheckCircle className="rpp-checkmark" />
                                            </div>
                                        </div>
                                        <h3>All Done!</h3>
                                        <p>
                                            Your password has been successfully updated. 
                                            Please use your new password to log in.
                                        </p>
                                        <button
                                            onClick={handleGoToLogin}
                                            className="rpp-btn rpp-btn-primary"
                                            tabIndex={1}
                                        >
                                            <FaLock aria-hidden="true" />
                                            Go to Login
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Password Change History Table */}
                    {step === 1 && passwordHistory.length > 0 && (
                        <div className="rpp-history-section" role="region" aria-label="Password change history">
                            <div className="rpp-card">
                                <div className="rpp-card-header">
                                    <FaInfoCircle className="rpp-card-icon" aria-hidden="true" />
                                    <h2>Password Change History</h2>
                                </div>
                                <div className="rpp-card-body">
                                    {/* Table Filter */}
                                    <div className="rpp-table-controls">
                                        <label htmlFor="rpp-filter" className="rpp-sr-only">
                                            Filter history
                                        </label>
                                        <input
                                            type="text"
                                            id="rpp-filter"
                                            className="rpp-filter-input"
                                            placeholder="Filter by date, method, or status..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            tabIndex={8}
                                            aria-label="Filter password change history"
                                        />
                                    </div>

                                    {/* Responsive Table */}
                                    <div className="rpp-table-wrapper" role="region" aria-label="Password change history table">
                                        <table className="rpp-table" aria-describedby="rpp-table-desc">
                                            <caption id="rpp-table-desc" className="rpp-sr-only">
                                                List of previous password change requests showing date, method, and status
                                            </caption>
                                            <thead>
                                                <tr>
                                                    <th scope="col">Date</th>
                                                    <th scope="col">Method</th>
                                                    <th scope="col">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentItems.length > 0 ? (
                                                    currentItems.map((item) => (
                                                        <tr key={item.id}>
                                                            <td data-label="Date">{item.date}</td>
                                                            <td data-label="Method">{item.method}</td>
                                                            <td data-label="Status">
                                                                <span className={`rpp-status rpp-status-${item.status.toLowerCase()}`}>
                                                                    {item.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={3} className="rpp-no-results">
                                                            No matching records found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav className="rpp-pagination" aria-label="Password history pagination">
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                className="rpp-page-btn"
                                                aria-label="Previous page"
                                            >
                                                ‹ Prev
                                            </button>
                                            <span className="rpp-page-info" aria-current="page">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                className="rpp-page-btn"
                                                aria-label="Next page"
                                            >
                                                Next ›
                                            </button>
                                        </nav>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </main>

                {/* Progress Modal */}
                {showModal && (
                    <div className="rpp-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rpp-modal-title">
                        <div className="rpp-modal">
                            <div className="rpp-modal-content">
                                {/* Modal Icon */}
                                <div className={`rpp-modal-icon-container rpp-modal-icon-${modalIcon}`}>
                                    {modalIcon === "loading" && (
                                        <div className="rpp-spinner" aria-hidden="true"></div>
                                    )}
                                    {modalIcon === "success" && (
                                        <FaCheckCircle className="rpp-modal-icon" aria-hidden="true" />
                                    )}
                                    {modalIcon === "error" && (
                                        <FaExclamationTriangle className="rpp-modal-icon" aria-hidden="true" />
                                    )}
                                </div>
                                
                                {/* Modal Title */}
                                <h4 id="rpp-modal-title">{modalMessage}</h4>
                                
                                {/* Progress Bar */}
                                <div className="rpp-progress-container">
                                    <div className="rpp-progress-bar">
                                        <div 
                                            className="rpp-progress-fill"
                                            style={{ width: `${progress}%` }}
                                            role="progressbar"
                                            aria-valuenow={progress}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                            aria-label={`Progress: ${progress}%`}
                                        ></div>
                                    </div>
                                    <span className="rpp-progress-text">{progress}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

export default ResetPasswordPage;
